import React, { useEffect, useState } from "react";
import styles from "./AdminSyncPage.module.scss";
import {
  getAdminServices,
  getAdminSyncActivity,
  getAdminSyncChanges,
  startAdminService,
  stopAdminService,
  triggerAdminSyncAll,
} from "../lib/api";
import { MOTION } from "../lib/animations";

const SOURCE_LABELS = {
  notion: "Notion",
  meta: "Meta",
  youtube: "YouTube",
};

const STATE_LABELS = {
  running: "Running",
  stopped: "Stopped",
  failed: "Failed",
};

const CHANGE_LABELS = {
  created: "Created",
  updated: "Updated",
  skipped: "Skipped",
  failed: "Failed",
};

const formatTime = (value) => {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Never";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const SyncActionButton = ({ disabled, label, onClick, pending }) => (
  <button
    className={styles.actionButton}
    data-motion={MOTION.adminActionButton}
    disabled={disabled || pending}
    onClick={onClick}
    type="button"
  >
    {pending ? "Running..." : label}
  </button>
);

export default function AdminSyncPage() {
  const [serviceRows, setServiceRows] = useState([]);
  const [activityRows, setActivityRows] = useState([]);
  const [changeRows, setChangeRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshError, setRefreshError] = useState("");
  const [actionError, setActionError] = useState("");
  const [pendingAction, setPendingAction] = useState("");
  const syncTokenPresent = Boolean(
    process.env.REACT_APP_SYNC_TOKEN || process.env.REACT_APP_API_TOKEN,
  );

  const refresh = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      setRefreshError("");
      const [servicesRes, activityRes, changesRes] = await Promise.all([
        getAdminServices(),
        getAdminSyncActivity(),
        getAdminSyncChanges(),
      ]);
      setServiceRows(Array.isArray(servicesRes?.data) ? servicesRes.data : []);
      setActivityRows(Array.isArray(activityRes?.data) ? activityRes.data : []);
      setChangeRows(Array.isArray(changesRes?.data) ? changesRes.data : []);
    } catch (error) {
      setRefreshError(error.message || "Failed to load admin sync data.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const timer = window.setInterval(() => {
      refresh({ silent: true });
    }, 15000);
    return () => window.clearInterval(timer);
  }, []);

  const runAction = async (actionKey, runner) => {
    setPendingAction(actionKey);
    setActionError("");
    try {
      await runner();
      await refresh();
    } catch (error) {
      setActionError(error.message || `Failed to run ${actionKey} sync.`);
    } finally {
      setPendingAction("");
    }
  };

  const runServiceControl = async (serviceId, action) => {
    const actionKey = `${serviceId}:${action}`;
    const runner = action === "start"
      ? () => startAdminService(serviceId)
      : () => stopAdminService(serviceId);
    return runAction(actionKey, runner);
  };

  if (!syncTokenPresent) {
    return (
      <section className={styles.page}>
        <div className={styles.shell}>
          <div className={styles.hero}>
            <p className={styles.eyebrow}>Internal Admin</p>
            <h1>Sync Dashboard</h1>
            <p className={styles.heroText}>
              This page requires `REACT_APP_SYNC_TOKEN` or `REACT_APP_API_TOKEN` in the frontend environment.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.page}>
      <div className={styles.shell}>
        <div className={styles.hero}>
          <div>
            <p className={styles.eyebrow}>Internal Admin</p>
            <h1>Sync Dashboard</h1>
            <p className={styles.heroText}>
              Operational view for the content API, database, and external sync integrations.
            </p>
          </div>
          <div className={styles.actionGrid}>
            <SyncActionButton
              disabled={loading}
              label="Sync All"
              onClick={() => runAction("all", triggerAdminSyncAll)}
              pending={pendingAction === "all"}
            />
            <SyncActionButton
              disabled={loading}
              label="Refresh"
              onClick={() => refresh()}
              pending={false}
            />
          </div>
        </div>

        {(refreshError || actionError) && (
          <div className={styles.errorBanner}>
            {refreshError || actionError}
          </div>
        )}

        <div className={styles.statusGrid}>
          {serviceRows.map((service) => {
            const startControl = service?.controls?.start || {};
            const stopControl = service?.controls?.stop || {};
            const state = service?.state || "stopped";
            return (
              <article key={service.id} className={styles.statusCard}>
                <div className={styles.statusHeader}>
                  <h2>{service.name}</h2>
                  <span className={`${styles.statusPill} ${styles[`status${state}`]}`}>
                    {STATE_LABELS[state] || state}
                  </span>
                </div>
                <p className={styles.serviceStatusText}>{service.current_status || "Unavailable"}</p>
                <dl className={styles.metrics}>
                  <div>
                    <dt>Last Checked</dt>
                    <dd>{formatTime(service.last_checked_at)}</dd>
                  </div>
                  <div>
                    <dt>Last Sync</dt>
                    <dd>{formatTime(service.last_sync_at)}</dd>
                  </div>
                  <div>
                    <dt>Last Activity</dt>
                    <dd>{formatTime(service.last_activity_at)}</dd>
                  </div>
                  <div>
                    <dt>Service Type</dt>
                    <dd>{service.kind || "service"}</dd>
                  </div>
                  {service.latest_run && (
                    <div>
                    <dt>Skipped</dt>
                    <dd>{service.latest_run.records_skipped ?? 0}</dd>
                    </div>
                  )}
                  {service.latest_run && (
                    <div>
                    <dt>Failed</dt>
                    <dd>{service.latest_run.records_failed ?? 0}</dd>
                    </div>
                  )}
                </dl>
                <div className={styles.serviceControls}>
                  {startControl.supported ? (
                    <SyncActionButton
                      disabled={loading || startControl.disabled}
                      label={state === "running" ? "Running" : "Start"}
                      onClick={() => runServiceControl(service.id, "start")}
                      pending={pendingAction === `${service.id}:start`}
                    />
                  ) : (
                    <p className={styles.controlNote}>
                      Start unavailable: {startControl.reason || "Not supported."}
                    </p>
                  )}
                  {stopControl.supported ? (
                    <SyncActionButton
                      disabled={loading || stopControl.disabled}
                      label="Stop"
                      onClick={() => runServiceControl(service.id, "stop")}
                      pending={pendingAction === `${service.id}:stop`}
                    />
                  ) : (
                    <p className={styles.controlNote}>
                      Stop unavailable: {stopControl.reason || "Not supported."}
                    </p>
                  )}
                </div>
                {service.error_message && (
                  <p className={styles.inlineError}>{service.error_message}</p>
                )}
              </article>
            );
          })}
        </div>

        <div className={styles.panelGrid}>
          <section className={styles.panel}>
            <div className={styles.panelHeader}>
              <h2>Recent Activity</h2>
              <button className={styles.refreshLink} onClick={() => refresh()} type="button">
                Refresh
              </button>
            </div>
            {loading ? (
              <p className={styles.emptyState}>Loading activity...</p>
            ) : activityRows.length ? (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Status</th>
                      <th>Started</th>
                      <th>Finished</th>
                      <th>Counts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityRows.map((row) => (
                      <tr key={row.id}>
                        <td>{SOURCE_LABELS[row.source] || row.source}</td>
                        <td>{row.status || "Unknown"}</td>
                        <td>{formatTime(row.started_at)}</td>
                        <td>{formatTime(row.finished_at)}</td>
                        <td>
                          C {row.records_created} / U {row.records_updated} / S {row.records_skipped} / F {row.records_failed}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className={styles.emptyState}>No sync runs recorded yet.</p>
            )}
          </section>

          <section className={styles.panel}>
            <div className={styles.panelHeader}>
              <h2>Recent Changes</h2>
            </div>
            {loading ? (
              <p className={styles.emptyState}>Loading changes...</p>
            ) : changeRows.length ? (
              <div className={styles.changeList}>
                {changeRows.map((row) => (
                  <article key={row.id} className={styles.changeItem}>
                    <div className={styles.changeTopRow}>
                      <span className={`${styles.changeBadge} ${styles[`change${row.action}`]}`}>
                        {CHANGE_LABELS[row.action] || row.action}
                      </span>
                      <span className={styles.changeSource}>
                        {SOURCE_LABELS[row.source] || row.source}
                      </span>
                    </div>
                    <p className={styles.changeSummary}>{row.summary || row.entity_id}</p>
                    <p className={styles.changeMeta}>
                      {row.entity_type} · {row.entity_id} · {formatTime(row.changed_at)}
                    </p>
                  </article>
                ))}
              </div>
            ) : (
              <p className={styles.emptyState}>No change history recorded yet.</p>
            )}
          </section>
        </div>
      </div>
    </section>
  );
}
